/* eslint-disable simple-import-sort/imports -- polyfill side-effect imports must be loaded before other modules */
import "./index.css";
import "core-js/actual/object/has-own";
import "core-js/proposals/string-replace-all-stage-4";
import "globalthis";
import "focus-visible";
import "./polyfills/abortSignalTimeout";
import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { resetThirdPartySettings } from "@next-platform/client-common-core/infra/utils/getConfigFromUrl";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { DOMAINS } from "@next-platform/client-common-core/infra/constants/domains";
import { addTrackJsModuleMetadata } from "@next-platform/client-common-core";
import initializeApp from "@/config/initializeApp";
import { KINDE_PROVIDER_CALLBACKS, migrateLegacyRefreshTokenToCookie } from "@/config/kinde";
import { APPLICATIONS_ID } from "@/constants";
import { registerTrackJsForMissingTranslationKeys } from "@/infra/reportMissingTranslationKeyToTrackJs";
import { isBrowserExtensionMessagingNoise } from "@/utils/isBrowserExtensionMessagingNoise";
import { reset3rdPartiesLocalSettings } from "@/utils/loadAsApplication";
import { applyNoIndexForNonProductionDomains } from "@/utils/seo";

import Routes from "./routes";
import store from "./store";
import {
  KINDE_CLIENT_ID,
  KINDE_DOMAIN,
  KINDE_USE_LOCAL_STORAGE,
  LOGROCKET_STAGING,
  TRACKJS_APPLICATION,
  TRACKJS_TOKEN,
} from "@next-platform/client-common-core/infra/constants";

type TrackJsLike = { track: (error: Error | string | object) => void };

type StartupErrorEntry = NonNullable<Window["__startupErrorQueue"]>[number];

const IS_LOCALHOST = location.host.indexOf("localhost") === 0;

function resolveTrackedPayload(entry: StartupErrorEntry): Error | string {
  if (entry.error instanceof Error) return entry.error;
  if (typeof entry.error === "string") return entry.error;
  return entry.message ?? `Startup ${entry.kind}`;
}

function flushStartupErrorQueue(trackJs: TrackJsLike): void {
  const queue = window.__startupErrorQueue;
  if (!queue || queue.length === 0) {
    window.__startupErrorQueue = [];
    return;
  }
  queue.forEach((entry) => {
    const payload = resolveTrackedPayload(entry);
    try {
      trackJs.track(payload);
    } catch {
      // Per-entry track failure is non-fatal; continue draining the queue.
    }
  });
  window.__startupErrorQueue = [];
}

// Initialize TrackJS asynchronously so its ~80 KB lands in the deferred
// "analytics" chunk instead of the boot critical path. TrackJS itself has
// no replay mechanism for errors thrown before `install()` runs, so a tiny
// inline listener in `index.html` (`window.__startupErrorQueue`) buffers
// everything from page load through React's initial render. Once TrackJS
// installs, we flush the queue via `TrackJS.track()`. From that point on,
// TrackJS's own global handlers take over.
async function initTrackJS(): Promise<void> {
  if (IS_LOCALHOST) return;
  try {
    const { TrackJS } = await import("trackjs");
    TrackJS.install({
      token: TRACKJS_TOKEN as string,
      application: TRACKJS_APPLICATION,
      version: basicAppConfig.appVersion,
      network: { enabled: false },

      onError(payload) {
        addTrackJsModuleMetadata(payload);

        return !isBrowserExtensionMessagingNoise(payload.message);
      },
    });
    flushStartupErrorQueue(TrackJS);
    registerTrackJsForMissingTranslationKeys({ trackJs: TrackJS });
  } catch {
    // Analytics failures are non-fatal.
  }
}

// Initialize Google Tag Manager asynchronously. GTM is ~50 KB and not on
// the rendering critical path — deferring it cuts that out of the boot bundle.
async function initGTM(): Promise<void> {
  try {
    const { ReactTagManager } = await import("react-gtm-ts");
    ReactTagManager.init({
      code: "GTM-NCBJ3WJ9",
      debug: false,
      performance: true,
    });
  } catch {
    // Analytics failures are non-fatal.
  }
}

async function initLogRocket(): Promise<void> {
  if (location.host.indexOf("staging") === -1) return;
  try {
    const [logrocketModule, { default: setupLogRocketReact }] = await Promise.all([import("logrocket"), import("logrocket-react")]);
    logrocketModule.default.init(LOGROCKET_STAGING as string);
    setupLogRocketReact(logrocketModule.default);
    window.LogRocket = logrocketModule.default;
  } catch {
    // Analytics failures are non-fatal.
  }
}

// Fallback delay for browsers without `requestIdleCallback` (Safari < 17.4).
// 1.5s is well past first paint on the slowest devices we target, so analytics
// initialization never lands on the rendering critical path.
const ANALYTICS_FALLBACK_DELAY_MS = 1500;
const ANALYTICS_IDLE_TIMEOUT_MS = 5000;

function fireAnalyticsInit(): void {
  void initTrackJS();
  void initGTM();
  void initLogRocket();
}

// Run analytics initialization when the browser is idle (after first paint
// and any urgent main-thread work). This keeps the analytics chunk OFF the
// boot critical path entirely on cold visits.
function scheduleAnalytics(): void {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(fireAnalyticsInit, { timeout: ANALYTICS_IDLE_TIMEOUT_MS });
  } else {
    setTimeout(fireAnalyticsInit, ANALYTICS_FALLBACK_DELAY_MS);
  }
}

if (!window.__UNSUPPORTED_BROWSER__) {
  scheduleAnalytics();

  resetThirdPartySettings(reset3rdPartiesLocalSettings);

  applyNoIndexForNonProductionDomains(window.location.hostname, DOMAINS);

  if (basicAppConfig.applicationId === APPLICATIONS_ID.SpicyChat) {
    import("@/config/firebase").catch(() => {
      // Firebase init failure is non-fatal; surfaces in console.
    });
  }

  const renderApp = (): void => {
    initializeApp();

    ReactDOM.createRoot(document.getElementById("root")!).render(
      <KindeProvider
        clientId={KINDE_CLIENT_ID as string}
        domain={KINDE_DOMAIN as string}
        logoutUri={window.location.origin}
        redirectUri={window.location.origin}
        useInsecureForRefreshToken={KINDE_USE_LOCAL_STORAGE === "true"}
        callbacks={KINDE_PROVIDER_CALLBACKS}
        refreshOnFocus={false}
      >
        <Provider store={store}>
          <Routes />
        </Provider>
      </KindeProvider>,
    );
  };

  const startApp = (): void => {
    if (import.meta.env.MODE === "e2e") {
      // Dynamic import keeps the purge module out of dev/uat/prod bundles via
      // Vite's dead-code elimination when MODE is statically replaced.
      import("@/debug/purgeE2eCaches")
        .then(({ default: purgeE2eCaches }) => {
          purgeE2eCaches();
          renderApp();
        })
        .catch(() => {
          renderApp();
        });
    } else {
      renderApp();
    }
  };

  if (KINDE_USE_LOCAL_STORAGE === "true") {
    startApp();
  } else {
    void migrateLegacyRefreshTokenToCookie({ domain: KINDE_DOMAIN as string, clientId: KINDE_CLIENT_ID as string }).then(startApp);
  }
}
